import type { InstrumentSlot } from "../types";
import {
  DEFAULT_INSTRUMENT_SLOT_COUNT,
  MAX_INSTRUMENT_SLOT_COUNT,
  allInstrumentSlots,
  clampInstrumentSlotCount,
} from "../types";
import { defaultSlotName } from "../kernel/defaultPresets";
import { formatFxList, parseFxList, parseFxToken } from "./fx";
import {
  formatNoteName,
  formatVolume,
  isEmptyNoteToken,
  isNoteOffToken,
  parseNoteName,
  parseVolumeToken,
} from "./notes";
import {
  createEmptyClip,
  emptyCell,
  setRowMarker,
  type ClipIR,
  type ParsePatternResult,
  type Pattern,
  type PatternCell,
  type PatternTrack,
  type RowMarker,
} from "./types";
import { normalizeClipScenes } from "./scenes";

/** Upper bound for a single pattern; 8192 fits long-form pieces (e.g. 340 bars of 3/4 at a 24th grid). */
export const MAX_CLIP_STEPS = 8192;

/**
 * Pattern DSL (hybrid):
 *
 * ```
 * @bpm 120
 * @grid 16
 * @steps 16
 * @loop on
 * I1 Bass
 * …
 * P0 Verse
 * @steps 64
 * #00 Intro
 * 00 C-2 80 .. | .... .. .. | …
 * P1 Chorus
 * @steps 32
 * 00 …
 * ```
 *
 * Global `@steps` is the default length; after a `P…` header, `@steps` sets that
 * pattern’s length. `P0 Verse` sets optional display name.
 * Per-pattern mute: `@mute 1 3` (1-based track numbers) after a `P…` header.
 * Row markers: `#<step> <label>` or `#<step> <label> <0-7>` (color index).
 */
export function parsePatternString(source: string): ParsePatternResult {
  const warnings: string[] = [];
  const clip = createEmptyClip();
  const instrumentNames: Partial<Record<InstrumentSlot, string>> = {};

  let defaultSteps = clip.patterns[0]!.steps;
  let currentPatternId: string | undefined;
  type Draft = {
    rows: string[];
    markers: Array<{ step: number; marker: RowMarker }>;
    /** 0-based track indices muted in this pattern. */
    mutedTracks: number[];
    steps?: number;
    name?: string;
  };
  const drafts = new Map<string, Draft>();

  const ensureDraft = (id: string): Draft => {
    let draft = drafts.get(id);
    if (!draft) {
      draft = { rows: [], markers: [], mutedTracks: [] };
      drafts.set(id, draft);
    }
    return draft;
  };

  const lines = source.split(/\r?\n/);
  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const lineNumber = lineIndex + 1;
    let line = lines[lineIndex] ?? "";
    const comment = line.indexOf(";");
    if (comment >= 0) {
      line = line.slice(0, comment);
    }
    line = line.trim();
    if (!line) {
      continue;
    }

    if (line.startsWith("@")) {
      const [key, ...rest] = line.slice(1).split(/\s+/);
      const value = rest.join(" ").trim();
      switch ((key ?? "").toLowerCase()) {
        case "bpm":
          clip.bpm = clampNumber(Number(value), 20, 400, 120);
          break;
        case "grid":
          clip.stepDivision = clampNumber(Number(value), 1, 64, 16);
          if (!source.includes("@steps")) {
            defaultSteps = clip.stepDivision;
            if (!currentPatternId) {
              clip.patterns[0]!.steps = defaultSteps;
            }
          }
          break;
        case "steps": {
          const next = clampNumber(Number(value), 1, MAX_CLIP_STEPS, 16);
          if (currentPatternId) {
            ensureDraft(currentPatternId).steps = next;
          } else {
            defaultSteps = next;
            clip.patterns[0]!.steps = next;
          }
          break;
        }
        case "loop":
          clip.loop = !/^(off|false|0|no)$/i.test(value);
          break;
        case "mute": {
          if (!currentPatternId) {
            currentPatternId = "P0";
            ensureDraft(currentPatternId);
          }
          const draft = ensureDraft(currentPatternId);
          draft.mutedTracks = [];
          for (const token of value.split(/\s+/).filter(Boolean)) {
            const oneBased = Number(token);
            if (!Number.isInteger(oneBased) || oneBased < 1 || oneBased > MAX_INSTRUMENT_SLOT_COUNT) {
              warnings.push(`Line ${lineNumber}: bad @mute track ${token}`);
              continue;
            }
            const slot = oneBased - 1;
            if (!draft.mutedTracks.includes(slot)) {
              draft.mutedTracks.push(slot);
            }
          }
          draft.mutedTracks.sort((a, b) => a - b);
          break;
        }
        default:
          warnings.push(`Line ${lineNumber}: unknown directive @${key}`);
      }
      continue;
    }

    const instrumentMatch = /^I(\d{1,3})\s+(\S.*)$/i.exec(line);
    if (instrumentMatch) {
      const oneBased = Number(instrumentMatch[1]);
      if (oneBased >= 1 && oneBased <= MAX_INSTRUMENT_SLOT_COUNT) {
        const slot = (oneBased - 1) as InstrumentSlot;
        instrumentNames[slot] = instrumentMatch[2]!.trim();
        continue;
      }
    }

    const patternMatch = /^P(\w+)(?:\s+(\S.*))?$/i.exec(line);
    if (patternMatch) {
      currentPatternId = `P${patternMatch[1]}`;
      const draft = ensureDraft(currentPatternId);
      const name = patternMatch[2]?.trim();
      if (name) {
        draft.name = name;
      }
      continue;
    }

    const markerMatch = /^#(\d+)\s+(.+)$/.exec(line);
    if (markerMatch) {
      if (!currentPatternId) {
        currentPatternId = "P0";
        ensureDraft(currentPatternId);
      }
      const step = Number(markerMatch[1]);
      const parsed = parseRowMarkerBody(markerMatch[2]!);
      if (!parsed) {
        warnings.push(`Line ${lineNumber}: empty row marker label`);
        continue;
      }
      if (step < 0 || step >= MAX_CLIP_STEPS) {
        warnings.push(`Line ${lineNumber}: row marker step out of range`);
        continue;
      }
      ensureDraft(currentPatternId).markers.push({ step, marker: parsed });
      continue;
    }

    if (/^\d+\b/.test(line)) {
      if (!currentPatternId) {
        currentPatternId = "P0";
        ensureDraft(currentPatternId);
      }
      ensureDraft(currentPatternId).rows.push(line);
      continue;
    }

    warnings.push(`Line ${lineNumber}: ignored: ${line}`);
  }

  applyInstrumentNames(clip, instrumentNames);

  if (drafts.size === 0) {
    const trackCount = inferTrackCountFromNames(instrumentNames, 1);
    ensureClipTrackCount(clip, trackCount);
    resizePatternSteps(clip.patterns[0]!, defaultSteps);
    applyInstrumentNames(clip, instrumentNames);
    return { clip, warnings };
  }

  const patterns: Pattern[] = [];
  for (const [id, draft] of drafts) {
    let steps = draft.steps ?? defaultSteps;
    let trackCount = inferTrackCountFromNames(instrumentNames, 1);
    const parsedRows: Array<{ step: number; cells: PatternCell[] }> = [];
    for (const row of draft.rows) {
      const parsed = parsePatternRow(row, warnings);
      if (!parsed) {
        continue;
      }
      trackCount = Math.max(trackCount, parsed.cells.length);
      parsedRows.push(parsed);
    }
    trackCount = clampInstrumentSlotCount(trackCount);
    const tracks = buildTracks(steps, instrumentNames, trackCount);
    for (const slot of draft.mutedTracks) {
      const track = tracks[slot];
      if (track) {
        track.muted = true;
      }
    }
    for (const parsed of parsedRows) {
      if (parsed.step >= steps) {
        steps = parsed.step + 1;
        for (const track of tracks) {
          while (track.cells.length < steps) {
            track.cells.push(emptyCell());
          }
        }
      }
      for (let t = 0; t < trackCount; t++) {
        const cell = parsed.cells[t];
        const track = tracks[t];
        if (cell && track && parsed.step < track.cells.length) {
          track.cells[parsed.step] = cell;
        }
      }
    }
    const pattern: Pattern = {
      id,
      steps,
      tracks,
      ...(draft.name ? { name: draft.name } : {}),
    };
    for (const entry of draft.markers) {
      if (entry.step < steps) {
        setRowMarker(pattern, entry.step, entry.marker);
      }
    }
    resizePatternSteps(pattern, steps);
    patterns.push(pattern);
  }

  clip.patterns = patterns;
  clip.order = patterns.map(pattern => pattern.id);
  clip.scenes = [];
  const finalCount = Math.max(
    1,
    ...patterns.map(p => p.tracks.length),
    inferTrackCountFromNames(instrumentNames, 0),
  );
  ensureClipTrackCount(clip, clampInstrumentSlotCount(finalCount));
  applyInstrumentNames(clip, instrumentNames);
  normalizeClipScenes(clip);

  return { clip, warnings };
}

export function stringifyPattern(clip: ClipIR): string {
  const lines: string[] = [
    `@bpm ${clip.bpm}`,
    `@grid ${clip.stepDivision}`,
    `@loop ${clip.loop ? "on" : "off"}`,
  ];

  const first = clip.patterns[0];
  if (first) {
    for (const track of first.tracks) {
      lines.push(`I${track.slot + 1} ${track.name ?? `Inst${track.slot + 1}`}`);
    }
  }

  lines.push("");

  for (const pattern of clip.patterns) {
    const steps = Math.max(1, pattern.steps || 1);
    lines.push(pattern.name?.trim() ? `${pattern.id} ${pattern.name.trim()}` : pattern.id);
    lines.push(`@steps ${steps}`);
    const mutedOneBased = pattern.tracks
      .filter(track => track.muted === true)
      .map(track => track.slot + 1);
    if (mutedOneBased.length > 0) {
      lines.push(`@mute ${mutedOneBased.join(" ")}`);
    }
    if (pattern.rowMarkers) {
      const markerSteps = Object.keys(pattern.rowMarkers)
        .map(Number)
        .filter(step => Number.isInteger(step) && step >= 0 && step < steps)
        .sort((a, b) => a - b);
      for (const step of markerSteps) {
        const marker = pattern.rowMarkers[String(step)];
        if (!marker?.label.trim()) {
          continue;
        }
        const color = Math.min(7, Math.max(0, Math.round(marker.color ?? 0)));
        const stepStr = String(step).padStart(2, "0");
        lines.push(
          color === 0
            ? `#${stepStr} ${marker.label.trim()}`
            : `#${stepStr} ${marker.label.trim()} ${color}`,
        );
      }
    }
    for (let step = 0; step < steps; step++) {
      const parts = pattern.tracks.map(track => formatCell(track.cells[step] ?? emptyCell()));
      lines.push(`${String(step).padStart(2, "0")} ${parts.join(" | ")}`);
    }
    lines.push("");
  }

  return lines.join("\n").trimEnd() + "\n";
}

function parseRowMarkerBody(body: string): RowMarker | undefined {
  const trimmed = body.trim();
  if (!trimmed) {
    return undefined;
  }
  const colorTail = /^(.*?)\s+([0-7])$/.exec(trimmed);
  if (colorTail && colorTail[1]!.trim()) {
    return {
      label: colorTail[1]!.trim(),
      color: Number(colorTail[2]),
    };
  }
  return { label: trimmed, color: 0 };
}

function formatCell(cell: PatternCell): string {
  if (cell.noteOff && cell.note === undefined) {
    return `--- ${formatVolume(cell.volume)} ${formatFxList(cell.fx)}`;
  }
  if (cell.note === undefined && !cell.noteOff) {
    const vol = cell.volume !== undefined || (cell.fx && cell.fx.length > 0)
      ? formatVolume(cell.volume)
      : "..";
    const fx = formatFxList(cell.fx);
    if (vol === ".." && fx === "..") {
      return ".... .. ..";
    }
    return `.... ${vol} ${fx}`;
  }
  return `${formatNoteName(cell.note!)} ${formatVolume(cell.volume)} ${formatFxList(cell.fx)}`;
}

function parsePatternRow(
  line: string,
  warnings: string[],
): { step: number; cells: PatternCell[] } | undefined {
  const stepMatch = /^(\d+)\s+(.*)$/.exec(line);
  if (!stepMatch) {
    warnings.push(`Bad row: ${line}`);
    return undefined;
  }
  const step = Number(stepMatch[1]);
  // Rows grow the clip, so an out-of-range index would allocate unbounded cells.
  if (step >= MAX_CLIP_STEPS) {
    warnings.push(`Row step out of range: ${line}`);
    return undefined;
  }
  const body = stepMatch[2] ?? "";
  const trackParts = body.split("|").map(part => part.trim());
  if (trackParts.length > MAX_INSTRUMENT_SLOT_COUNT) {
    warnings.push(`Row has more than ${MAX_INSTRUMENT_SLOT_COUNT} tracks; extras ignored`);
  }
  const cells = trackParts
    .slice(0, MAX_INSTRUMENT_SLOT_COUNT)
    .map(part => parseTrackCell(part, warnings));
  return { step, cells };
}

function parseTrackCell(text: string, warnings: string[]): PatternCell {
  const tokens = text.trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) {
    return emptyCell();
  }

  const cell: PatternCell = {};
  const noteTok = tokens[0] ?? "....";

  if (isNoteOffToken(noteTok)) {
    cell.noteOff = true;
  } else if (!isEmptyNoteToken(noteTok)) {
    const note = parseNoteName(noteTok);
    if (note === undefined) {
      warnings.push(`Bad note token: ${noteTok}`);
    } else {
      cell.note = note;
    }
  }

  if (tokens[1]) {
    const volume = parseVolumeToken(tokens[1]);
    if (volume !== undefined) {
      cell.volume = volume;
    } else if (tokens[1] !== ".." && tokens[1] !== ".") {
      warnings.push(`Bad volume token: ${tokens[1]}`);
    }
  }

  const fx: NonNullable<PatternCell["fx"]> = [];
  for (const token of tokens.slice(2)) {
    if (token === ".." || token === "." || /^\.+$/.test(token)) {
      continue;
    }
    // Glued FX (`C--BFF`, `...B00` under a later column) via parseFxList.
    const glued = parseFxList(token);
    if (glued.length > 0) {
      fx.push(...glued);
      continue;
    }
    const parsed = parseFxToken(token);
    if (parsed) {
      fx.push(parsed);
    } else {
      warnings.push(`Bad FX token: ${token}`);
    }
  }
  if (fx.length > 0) {
    cell.fx = fx;
  }
  return cell;
}

function inferTrackCountFromNames(
  names: Partial<Record<InstrumentSlot, string>>,
  fallback: number,
): number {
  let max = fallback;
  for (const key of Object.keys(names)) {
    const slot = Number(key);
    if (Number.isInteger(slot) && slot >= 0) {
      max = Math.max(max, slot + 1);
    }
  }
  return clampInstrumentSlotCount(max);
}

function buildTracks(
  steps: number,
  names: Partial<Record<InstrumentSlot, string>>,
  trackCount: number = DEFAULT_INSTRUMENT_SLOT_COUNT,
): PatternTrack[] {
  return allInstrumentSlots(trackCount).map(slot => ({
    slot,
    name: names[slot] ?? defaultSlotName(slot),
    cells: Array.from({ length: steps }, () => emptyCell()),
  }));
}

function applyInstrumentNames(
  clip: ClipIR,
  names: Partial<Record<InstrumentSlot, string>>,
): void {
  for (const pattern of clip.patterns) {
    for (const track of pattern.tracks) {
      if (names[track.slot]) {
        track.name = names[track.slot];
      }
    }
  }
}

/** Pad or trim each pattern to `trackCount` tracks (legacy short clips pad up). */
export function ensureClipTrackCount(
  clip: ClipIR,
  trackCount: number = DEFAULT_INSTRUMENT_SLOT_COUNT,
): void {
  const n = clampInstrumentSlotCount(trackCount);
  for (const pattern of clip.patterns) {
    const steps = Math.max(1, pattern.steps || 1);
    pattern.steps = steps;
    while (pattern.tracks.length < n) {
      const slot = pattern.tracks.length as InstrumentSlot;
      pattern.tracks.push({
        slot,
        name: defaultSlotName(slot),
        cells: Array.from({ length: steps }, () => emptyCell()),
      });
    }
    if (pattern.tracks.length > n) {
      pattern.tracks.length = n;
    }
    for (let i = 0; i < pattern.tracks.length; i++) {
      pattern.tracks[i]!.slot = i as InstrumentSlot;
    }
  }
}

/** Resize a single pattern’s row count (pads/trims cells + markers). Keeps track count. */
export function resizePatternSteps(pattern: Pattern, steps: number): void {
  const next = Math.min(MAX_CLIP_STEPS, Math.max(1, Math.round(steps)));
  pattern.steps = next;
  const trackCount = Math.max(1, pattern.tracks.length || DEFAULT_INSTRUMENT_SLOT_COUNT);
  while (pattern.tracks.length < trackCount) {
    const slot = pattern.tracks.length as InstrumentSlot;
    pattern.tracks.push({
      slot,
      name: defaultSlotName(slot),
      cells: Array.from({ length: next }, () => emptyCell()),
    });
  }
  for (let i = 0; i < pattern.tracks.length; i++) {
    const track = pattern.tracks[i]!;
    track.slot = i as InstrumentSlot;
    if (track.cells.length < next) {
      while (track.cells.length < next) {
        track.cells.push(emptyCell());
      }
    } else if (track.cells.length > next) {
      track.cells.length = next;
    }
  }
  if (pattern.rowMarkers) {
    for (const key of Object.keys(pattern.rowMarkers)) {
      const step = Number(key);
      if (!Number.isInteger(step) || step < 0 || step >= next) {
        delete pattern.rowMarkers[key];
      }
    }
    if (Object.keys(pattern.rowMarkers).length === 0) {
      delete pattern.rowMarkers;
    }
  }
}

/** Resize every pattern in the clip to the same length (legacy helper). */
export function resizeClipSteps(clip: ClipIR, steps: number): void {
  const n = clip.patterns[0]?.tracks.length ?? DEFAULT_INSTRUMENT_SLOT_COUNT;
  ensureClipTrackCount(clip, n);
  for (const pattern of clip.patterns) {
    resizePatternSteps(pattern, steps);
  }
}

function clampNumber(value: number, min: number, max: number, fallback: number): number {
  if (!Number.isFinite(value)) {
    return fallback;
  }
  return Math.min(max, Math.max(min, Math.round(value)));
}
